/**
 * Game Core Class - Main game logic and scene management
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { SceneManager } from './SceneManager.js';
import { PhysicsSystem } from '../systems/PhysicsSystem.js';
import { RenderSystem } from '../systems/RenderSystem.js';
import { InputManager } from './InputManager.js';
import { PlayerShip } from '../entities/PlayerShip.js';
import { EnemyManager } from '../systems/EnemyManager.js';
import { ProjectileManager } from '../systems/ProjectileManager.js';
import { RankingSystem } from '../systems/RankingSystem.js';
import { RankingUI } from '../ui/RankingUI.js';
import { CoinSystem } from '../systems/CoinSystem.js';
import { HealthPackSystem } from '../systems/HealthPackSystem.js';
import { EnemyUIManager } from '../ui/EnemyUIManager.js';
import { CoinAnimationUI } from '../ui/CoinAnimationUI.js';
import { MobileStatsOverlay } from '../ui/MobileStatsOverlay.js';
import { RescueManager } from '../systems/RescueManager.js';
import { AudioManager } from '../systems/AudioManager.js';
import { RewardManager } from '../systems/RewardManager.js';
import { LanguageManager } from '../systems/LanguageManager.js';
import { PerformanceLogger } from '../utils/PerformanceLogger.js';

export class Game {
    constructor(options = {}) {
        this.canvas = options.canvas;
        this.debug = options.debug || false;

        // Game state
        this.isRunning = false;
        this.isPaused = false;
        this.gameTime = 0;
        this.deltaTime = 0;
        this.lastTime = 0;

        // Core systems
        this.sceneManager = null;
        this.physicsSystem = null;
        this.renderSystem = null;
        this.inputManager = null;

        // Game managers
        this.enemyManager = null;
        this.projectileManager = null;
        this.rankingSystem = null;
        this.rankingUI = null;
        this.coinSystem = null;
        this.healthPackSystem = null;
        this.enemyUIManager = null;
        this.coinAnimationUI = null;
        this.mobileStatsOverlay = null;
        this.rescueManager = null;
        this.audioManager = new AudioManager();
        this.rewardManager = null;
        this.languageManager = new LanguageManager();

        // Game entities
        this.player = null;
        this.gameObjects = new Map();

        this.perfLogger = new PerformanceLogger();

        // Game configuration
        this.config = {
            worldSize: 100, // 更新为匹配新的边界大小 (±100 = 200x200 总区域)
            maxEnemies: 10,
            gameTime: 300, // 5 minutes
            physics: {
                gravity: 0,
                timestep: 1/60,
                maxSubSteps: 3
            }
        };

        // Respawn system
        this.respawnDelay = 1000; // 1 second respawn delay for downtime
        this.isRespawning = false;

        // Ranking display
        this.lastRankingDisplay = 0;
        this.rankingDisplayInterval = 30000; // Show ranking every 30 seconds

        // Debug monitoring
        this.lastDebugTime = -1;

        // Bind methods
        this.update = this.update.bind(this);
        this.handleResize = this.handleResize.bind(this);
    }

    async init() {

        try {
            // Initialize core systems
            console.log('🔧 创建物理系统...');
            this.physicsSystem = new PhysicsSystem(this.config.physics);
            console.log('- 物理系统创建完成:', !!this.physicsSystem);
            
            console.log('🔧 初始化物理系统...');
            this.physicsSystem.init();
            console.log('- 物理世界创建状态:', !!this.physicsSystem.world);
            console.log('- 物理世界对象:', this.physicsSystem.world);
            
            console.log('🔧 创建场景管理器...');
            this.sceneManager = new SceneManager(this.physicsSystem);
            console.log('- 场景管理器创建完成:', !!this.sceneManager);
            
            this.renderSystem = new RenderSystem(this.canvas);
            this.inputManager = new InputManager();

            // Initialize systems
            this.renderSystem.init();

            // Initialize managers
            this.enemyManager = new EnemyManager(this);
            this.projectileManager = new ProjectileManager(this);
            this.rankingSystem = new RankingSystem(this);
            this.rankingUI = new RankingUI(this);
            this.coinSystem = new CoinSystem(this);
            this.healthPackSystem = new HealthPackSystem(this);
            this.enemyUIManager = new EnemyUIManager(this);
            this.coinAnimationUI = new CoinAnimationUI(this);
            this.mobileStatsOverlay = new MobileStatsOverlay(this);
            this.rescueManager = new RescueManager(this);
            this.rewardManager = new RewardManager(this);
            console.log('📺 奖励视频管理器已初始化');

            // Update DOM with current language
            this.languageManager.updateDOM();
            console.log(`🌐 语言系统已初始化: ${this.languageManager.getCurrentLanguage()}`);

            // Setup scene
            await this.sceneManager.init();
            this.renderSystem.setScene(this.sceneManager.scene);
            this.renderSystem.setCamera(this.sceneManager.camera);

            if (this.rescueManager) {
                await this.rescueManager.init();
            }

            // Create player ship
            await this.createPlayer();

            // Setup input handlers
            this.setupInputHandlers();

            // Initial resize
            this.handleResize();


        } catch (error) {
            console.error('❌ Failed to initialize game core:', error);
            throw error;
        }
    }

    async createPlayer() {
        // 设置玩家初始位置在安全的水域
        const safePosition = this.findSafeSpawnPosition();
        this.player = new PlayerShip({
            position: safePosition,
            game: this
        });

        // Wait for player initialization to complete
        await this.player.init();

        this.addGameObject('player', this.player);

        // Set camera to follow player
        this.sceneManager.setFollowTarget(this.player.mesh);
        
        console.log(`玩家初始位置: (${safePosition.x}, ${safePosition.y}, ${safePosition.z})`);
    }

    setupInputHandlers() {
        // Mouse movement for ship control
        this.inputManager.onMouseMove = (mousePos) => {
            if (this.player && this.isRunning) {
                this.player.setTargetPosition(mousePos);
            }
        };

        // Mouse click for shooting
        this.inputManager.onMouseClick = (button, action, position) => {
            if (button === 0 && action === 'down' && this.player && this.isRunning) { // Left click down
                this.player.shoot();
            }
        };

        // Number keys for special abilities
        this.inputManager.onKeyPress = (key) => {
            if (this.player && this.isRunning) {
                const abilityIndex = parseInt(key) - 1;
                if (abilityIndex >= 0 && abilityIndex < 5) {
                    this.player.useAbility(abilityIndex);
                }
            }
        };
    }

    start() {
        if (!this.isRunning) {
            this.isRunning = true;
            this.isPaused = false;
            this.lastTime = performance.now();

            // Start game systems
            this.enemyManager.start();

            // Show ranking UI when game starts
            if (this.rankingUI) {
                this.rankingUI.show();
            }

            // Start game loop
            this.update();

        }
    }

    pause() {
        this.isPaused = true;
    }

    resume() {
        if (this.isPaused) {
            this.isPaused = false;
            this.lastTime = performance.now();
        }
    }

    stop() {
        this.isRunning = false;
        this.isPaused = false;

        // Stop all systems
        this.enemyManager.stop();

        if (this.rescueManager) {
            this.rescueManager.onPlayerDeath();
        }

        // Hide ranking UI
        if (this.rankingUI) {
            this.rankingUI.hide();
        }

        // Reset game state
        this.gameTime = 0;

    }

    // Restart the game
    restart() {
        // Stop current game
        this.stop();

        // Reset player stats
        if (this.rankingSystem) {
            this.rankingSystem.resetPlayerStats();
        }

        // Reset player ship
        if (this.player) {
            this.player.level = 1;
            this.player.score = 0;
            this.player.healToFull();
        }

        // Clear enemies
        this.enemyManager.clearEnemies();

        // Clear coins
        if (this.coinSystem) {
            this.coinSystem.clearCoins();
        }

        // Clear health packs
        if (this.healthPackSystem) {
            this.healthPackSystem.clearHealthPacks();
        }

        // Reset respawn state
        this.isRespawning = false;

        // Find new spawn position
        const spawnPosition = this.findSafeSpawnPosition();
        if (this.player) {
            this.player.respawn(spawnPosition);
        }

        // Start game again
        this.start();
    }

    update() {
        if (!this.isRunning) return;

        // Calculate delta time
        const currentTime = performance.now();
        this.deltaTime = (currentTime - this.lastTime) / 1000;
        this.lastTime = currentTime;

        if (this.deltaTime > 0 && typeof window !== 'undefined' && window.gameApp?.uiManager?.updateFPS) {
            window.gameApp.uiManager.updateFPS(1 / this.deltaTime);
        }

        if (this.isPaused) {
            requestAnimationFrame(this.update);
            return;
        }

        // Update game time
        this.gameTime += this.deltaTime;

        // 监控内存使用
        if (Math.floor(this.gameTime) % 10 === 0 && Math.floor(this.gameTime) !== this.lastDebugTime) {
            this.lastDebugTime = Math.floor(this.gameTime);
        }

        const perf = this.perfLogger;

        // Update systems
        perf.wrap('input', () => {
            this.inputManager.update();
        });

        let physicsStats = null;
        let renderInfo = null;
        perf.wrap('physics', () => {
            this.physicsSystem.update(this.deltaTime);
            physicsStats = this.physicsSystem.getStepStats?.();
        });

        const objectsToRemove = [];
        perf.wrap('objects:update', () => {
            this.gameObjects.forEach((gameObject, id) => {
                if (gameObject.update) {
                    gameObject.update(this.deltaTime);
                }
                if (gameObject.shouldRemove) {
                    objectsToRemove.push(id);
                }
            });
        });

        perf.wrap('objects:cleanup', () => {
            objectsToRemove.forEach(id => {
                this.removeGameObject(id);
            });
        });

        perf.wrap('enemyManager', () => {
            this.enemyManager.update(this.deltaTime);
        });

        perf.wrap('projectiles', () => {
            this.projectileManager.update(this.deltaTime);
        });

        // Update ranking system
        perf.wrap('ranking', () => {
            if (this.rankingSystem) {
                this.rankingSystem.updateSurvivalTime(this.deltaTime);

                const now = Date.now();
                if (now - this.lastRankingDisplay > this.rankingDisplayInterval) {
                    this.rankingSystem.displayRanking();
                    this.lastRankingDisplay = now;
                }
            }
        });

        perf.wrap('rankingUI', () => {
            if (this.rankingUI) {
                this.rankingUI.update(this.deltaTime);
            }
        });

        perf.wrap('coinSystem', () => {
            if (this.coinSystem) {
                this.coinSystem.update(this.deltaTime);
            }
        });

        perf.wrap('healthPackSystem', () => {
            if (this.healthPackSystem) {
                this.healthPackSystem.update(this.deltaTime);
            }
        });

        perf.wrap('rescueManager', () => {
            if (this.rescueManager) {
                this.rescueManager.update(this.deltaTime);
            }
        });

        perf.wrap('enemyUI', () => {
            if (this.enemyUIManager) {
                this.enemyUIManager.update(this.deltaTime);
            }
        });

        perf.wrap('coinFX', () => {
            if (this.coinAnimationUI) {
                this.coinAnimationUI.update();
            }
        });

        perf.wrap('mobileStats', () => {
            if (this.mobileStatsOverlay) {
                this.mobileStatsOverlay.update();
            }
        });

        perf.wrap('scene', () => {
            this.sceneManager.update(this.deltaTime);
        });

        perf.wrap('render', () => {
            this.renderSystem.render();
            renderInfo = this.renderSystem.renderer?.info?.render ?? null;
        });

        if (physicsStats) {
            perf.accumulate('physicsContacts', physicsStats.contacts ?? 0);
            perf.accumulate('physicsBodies', physicsStats.bodies ?? 0);
            perf.accumulate('physicsActiveBodies', physicsStats.active ?? 0);
        }

        if (renderInfo) {
            perf.accumulate('renderTriangles', renderInfo.triangles ?? 0);
            perf.accumulate('renderCalls', renderInfo.calls ?? 0);
        }

        const centralBounds = this.sceneManager?.centralIslandBounds;
        const playerBody = this.player?.body;
        if (centralBounds && playerBody) {
            const dx = playerBody.position.x - centralBounds.center.x;
            const dz = playerBody.position.z - centralBounds.center.z;
            const horizontalDist = Math.sqrt(dx * dx + dz * dz);
            const edgeDist = horizontalDist - (centralBounds.radius ?? 0);
            perf.accumulate('playerIslandDist', horizontalDist);
            perf.accumulate('playerIslandEdge', edgeDist);
        }

        perf.accumulate('enemyCount', this.enemyManager?.enemies?.size ?? 0);
        perf.accumulate('pendingEnemySpawns', this.enemyManager?.pendingSpawns?.length ?? 0);
        perf.accumulate('gameObjects', this.gameObjects.size);

        // Update game status UI (throttled to every 0.2 seconds)
        if (!this._lastStatusUpdate || (currentTime - this._lastStatusUpdate) > 200) {
            this._lastStatusUpdate = currentTime;
            if (typeof window !== 'undefined' && window.gameApp?.uiManager?.updateGameStatus) {
                const kills = this.rankingSystem?.getPlayerKills?.() ?? 0;
                const gold = this.coinSystem?.getPlayerCoins?.() ?? 0;
                const enemies = this.enemyManager?.enemies?.size ?? 0;

                window.gameApp.uiManager.updateGameStatus({
                    kills,
                    gold,
                    enemies
                });
            }
        }

        // Continue game loop
        perf.frameEnd();
        requestAnimationFrame(this.update);
    }

    addGameObject(id, gameObject) {
        this.gameObjects.set(id, gameObject);

        if (gameObject.mesh) {
            this.sceneManager.scene.add(gameObject.mesh);
        }

        if (gameObject.body) {
            this.physicsSystem.addBody(gameObject.body);
        }
    }

    removeGameObject(id) {
        const gameObject = this.gameObjects.get(id);
        if (gameObject) {
            if (gameObject.mesh) {
                this.sceneManager.scene.remove(gameObject.mesh);
            }

            if (gameObject.body) {
                this.physicsSystem.removeBody(gameObject.body);
            }

            if (gameObject.dispose) {
                gameObject.dispose();
            }

            this.gameObjects.delete(id);
        }
    }

    getGameObject(id) {
        return this.gameObjects.get(id);
    }

    handleResize() {
        if (this.renderSystem) {
            this.renderSystem.handleResize();
        }

        if (this.sceneManager) {
            this.sceneManager.handleResize();
        }
    }

    // Get world position from screen coordinates
    screenToWorld(screenX, screenY) {
        return this.sceneManager.screenToWorld(screenX, screenY);
    }

    // 检查位置是否在游戏边界内
    isPositionInBounds(position) {
        return this.sceneManager.isWithinGameBounds(position);
    }

    // 将位置限制在游戏边界内
    clampPositionToBounds(position) {
        return this.sceneManager.clampToGameBounds(position);
    }

    // 获取游戏边界信息 (供其他系统使用)
    getGameBounds() {
        return this.sceneManager.getGameBounds();
    }

    // Player respawn system
    respawnPlayer() {
        if (this.isRespawning) return;

        this.isRespawning = true;

        // Record death in ranking system
        if (this.rankingSystem) {
            this.rankingSystem.recordPlayerDeath();
            if (typeof this.rankingSystem.resetKillCount === 'function') {
                this.rankingSystem.resetKillCount();
            }
        }

        if (this.rescueManager) {
            this.rescueManager.onPlayerDeath();
        }

        // 重置奖励视频计数（死亡后救援计数从0开始）
        if (this.rewardManager) {
            this.rewardManager.resetRescueCount();
        }

        // 暂时隐藏旧船体，避免复活等待期间继续显示
        if (this.player) {
            if (this.player.mesh) {
                this.player.mesh.visible = false;
            }
            if (this.player.body) {
                this.player.body.velocity.set(0, 0, 0);
                this.player.body.angularVelocity.set(0, 0, 0);
                if (typeof this.player.body.sleep === 'function') {
                    this.player.body.sleep();
                }
            }
        }

        // Apply coin penalty on death (lose 30% rounded to nearest integer)
        if (this.coinSystem && typeof this.coinSystem.getPlayerCoins === 'function') {
            const currentCoins = this.coinSystem.getPlayerCoins();
            const penalty = Math.round(currentCoins * 0.3);
            if (penalty > 0 && typeof this.coinSystem.loseCoins === 'function') {
                const lost = this.coinSystem.loseCoins(penalty);
                if (lost > 0) {
                    console.log(`Applied death coin penalty: -${lost} coins (from ${currentCoins})`);
                }
            }
        }

        // Show death notification
        if (this.rankingUI) {
            this.rankingUI.showDeathNotification();
        }

        // Temporarily disable player input
        if (this.inputManager) {
            this.inputManager.isEnabled = false;
        }

        // Find a safe respawn position
        const respawnPosition = this.findSafeRespawnPosition();

        const performRespawn = () => {
            try {
                if (this.player) {
                    // Respawn the player
                    this.player.respawn(respawnPosition);
                    console.log('Player respawned successfully'); // 调试信息
                }
            } catch (error) {
                console.error('Error during respawn:', error);
            } finally {
                // 无论如何都要重新启用输入，避免卡住
                if (this.inputManager) {
                    this.inputManager.isEnabled = true;
                }
                this.isRespawning = false;
            }
        };

        if (this.respawnDelay > 0) {
            setTimeout(performRespawn, this.respawnDelay);
        } else {
            performRespawn();
        }
    }

    findSafeRespawnPosition() {
        // 尝试多次找到安全的刷新位置
        for (let attempts = 0; attempts < 50; attempts++) {
            // 在游戏边界内随机生成位置，但远离岛屿
            const angle = Math.random() * Math.PI * 2;
            const distance = 30 + Math.random() * 40; // 距离中心30-70单位
            
            const position = {
                x: Math.cos(angle) * distance,
                y: 0.1,
                z: Math.sin(angle) * distance
            };

            // 检查是否在游戏边界内
            if (!this.sceneManager.isWithinGameBounds(position)) {
                continue;
            }

            // 使用射线检测确保位置在水面上，不在岛屿内部
            if (this.isPositionSafe(position)) {
                return position;
            }
        }

        // 如果找不到安全位置，返回一个远离岛屿的默认位置
        console.warn('无法找到安全的刷新位置，使用默认位置');
        return { x: -50, y: 0.1, z: -50 };
    }

    findSafeSpawnPosition() {
        return this.findSafeRespawnPosition();
    }

    isPositionSafe(position) {
        if (!this.physicsSystem) return true;

        // 使用射线检测从上方向下检测是否有岛屿碰撞
        const from = new CANNON.Vec3(position.x, 10, position.z);
        const to = new CANNON.Vec3(position.x, -5, position.z);
        
        const result = this.physicsSystem.raycast(from, to, {
            collisionFilterMask: 1 | 8 // 检测岛屿和建筑物
        });

        // 如果射线没有碰撞到任何物体，说明位置安全（在水面上）
        return !result.hasHit;
    }

    // Debug methods
    enableDebug() {
        this.debug = true;
        this.physicsSystem.enableDebug();
    }

    disableDebug() {
        this.debug = false;
        this.physicsSystem.disableDebug();
    }
}
